import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/shared/auth/useAuth";
import { tourRegistry } from "./tours";
import type {
  ChecklistItem,
  OnboardingContextValue,
  OnboardingState,
  TourDefinition,
} from "./types";

const CHECKLIST_DEFINITIONS: Omit<ChecklistItem, "completed">[] = [
  { id: "create-location", label: "Create your first location", labelKey: "onboarding.checklist.createLocation", descriptionKey: "onboarding.checklist.createLocationDesc", href: "/#/locations", tourId: "locations", icon: "MapPin" },
  { id: "add-asset", label: "Register an asset", labelKey: "onboarding.checklist.addAsset", descriptionKey: "onboarding.checklist.addAssetDesc", href: "/#/assets", tourId: "assets", icon: "Package" },
  { id: "create-work-order", label: "Create a work order", labelKey: "onboarding.checklist.createWorkOrder", descriptionKey: "onboarding.checklist.createWorkOrderDesc", href: "/#/work-orders", tourId: "work-orders", icon: "Wrench" },
  { id: "setup-pm-schedule", label: "Set up preventive maintenance", labelKey: "onboarding.checklist.setupPm", descriptionKey: "onboarding.checklist.setupPmDesc", href: "/#/pm-schedules", tourId: "pm-schedules", icon: "CalendarClock" },
  { id: "run-inspection", label: "Run your first inspection", labelKey: "onboarding.checklist.runInspection", descriptionKey: "onboarding.checklist.runInspectionDesc", href: "/#/inspections", tourId: "inspections", icon: "ClipboardCheck" },
  { id: "view-dashboard", label: "Review your dashboard", labelKey: "onboarding.checklist.viewDashboard", descriptionKey: "onboarding.checklist.viewDashboardDesc", href: "/#/", icon: "LayoutDashboard" },
];

const DEFAULT_STATE: OnboardingState = {
  checklistDismissed: false,
  checklistItems: {},
  completedTours: [],
};

/** Debounce delay before persisting to DB (ms) */
const SAVE_DEBOUNCE_MS = 500;

/**
 * Poll for a DOM element matching `selector` every 100ms, up to `timeoutMs`.
 * Resolves with the element or null if not found in time.
 */
function waitForElement(selector: string, timeoutMs = 3000): Promise<Element | null> {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      const found = document.querySelector(selector);
      if (found) {
        clearInterval(interval);
        resolve(found);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        resolve(null);
      }
    }, 100);
  });
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

const NOOP_CONTEXT: OnboardingContextValue = {
  checklistItems: [],
  checklistDismissed: true,
  completeChecklistItem: () => {},
  dismissChecklist: () => {},
  showChecklist: () => {},
  checklistProgress: 0,
  activeTour: null,
  currentStepIndex: 0,
  isTouring: false,
  startTour: () => {},
  startModuleTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipTour: () => {},
  completeTour: () => {},
  isTourCompleted: () => false,
  resetOnboarding: () => {},
};

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  return ctx ?? NOOP_CONTEXT;
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { preferences, updatePreferences } = useAuth();
  const navigate = useNavigate();

  // Initialize state from DB preferences (if loaded) or default
  const [state, setState] = useState<OnboardingState>(
    () => preferences?.onboarding ?? { ...DEFAULT_STATE }
  );

  // Tour state
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepReady, setStepReady] = useState(true);

  // Track active tour for callbacks
  const activeTourRef = useRef(activeTour);
  activeTourRef.current = activeTour;

  // Sync state from DB when preferences load/change (e.g. after login)
  const prevPrefsRef = useRef(preferences?.onboarding);
  useEffect(() => {
    const dbState = preferences?.onboarding;
    if (dbState && dbState !== prevPrefsRef.current) {
      setState(dbState);
      prevPrefsRef.current = dbState;
    }
  }, [preferences?.onboarding]);

  // Debounced persist to DB
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const persistToDb = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updatePreferences({ onboarding: stateRef.current });
    }, SAVE_DEBOUNCE_MS);
  }, [updatePreferences]);

  // Persist on state change (debounced)
  useEffect(() => {
    persistToDb();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [state, persistToDb]);

  // Handle step navigation/waiting when step changes
  useEffect(() => {
    if (!activeTour) return;
    const step = activeTour.steps[currentStepIndex];
    if (!step) return;

    // Only go async if step requires navigation or waiting for a selector.
    const needsAsync = !!(step.navigateTo || step.waitForSelector);
    if (!needsAsync) {
      setStepReady(true);
      return;
    }

    let cancelled = false;

    const prepareStep = async () => {
      if (step.navigateTo) {
        navigate(step.navigateTo);
        await new Promise((r) => setTimeout(r, 300));
      }

      if (cancelled) return;

      if (step.waitForSelector) {
        await waitForElement(step.waitForSelector);
      }

      if (cancelled) return;
      setStepReady(true);
    };

    setStepReady(false);
    prepareStep();

    return () => {
      cancelled = true;
    };
  }, [activeTour, currentStepIndex, navigate]);

  // --- Checklist ---

  const checklistItems: ChecklistItem[] = useMemo(
    () =>
      CHECKLIST_DEFINITIONS.map((def) => ({
        ...def,
        completed: !!state.checklistItems[def.id],
      })),
    [state.checklistItems],
  );

  const checklistProgress = useMemo(() => {
    const done = checklistItems.filter((i) => i.completed).length;
    return checklistItems.length > 0 ? done / checklistItems.length : 0;
  }, [checklistItems]);

  const completeChecklistItem = useCallback((itemId: string) => {
    setState((prev) => ({
      ...prev,
      checklistItems: { ...prev.checklistItems, [itemId]: true },
    }));
  }, []);

  const dismissChecklist = useCallback(() => {
    setState((prev) => ({ ...prev, checklistDismissed: true }));
  }, []);

  const showChecklist = useCallback(() => {
    setState((prev) => ({ ...prev, checklistDismissed: false }));
  }, []);

  // --- Tours ---

  const markTourComplete = useCallback(
    (tourId: string) => {
      setState((prev) => {
        const nextState = {
          ...prev,
          completedTours: prev.completedTours.includes(tourId)
            ? prev.completedTours
            : [...prev.completedTours, tourId],
        };
        const matchingItem = CHECKLIST_DEFINITIONS.find((def) => def.tourId === tourId);
        if (matchingItem) {
          nextState.checklistItems = {
            ...nextState.checklistItems,
            [matchingItem.id]: true,
          };
        }
        return nextState;
      });
    },
    [],
  );

  const startTour = useCallback((tourId: string) => {
    const tour = tourRegistry[tourId];
    if (!tour) return;
    setActiveTour(tour);
    setCurrentStepIndex(0);
  }, []);

  const startModuleTour = useCallback(
    (tourId: string) => {
      const tour = tourRegistry[tourId];
      if (!tour) return;
      setActiveTour(tour);
      setCurrentStepIndex(0);
    },
    [],
  );

  const nextStep = useCallback(() => {
    if (!activeTourRef.current) return;
    const tour = activeTourRef.current;
    if (currentStepIndex < tour.steps.length - 1) {
      setCurrentStepIndex((i) => i + 1);
    } else {
      markTourComplete(tour.id);
      setActiveTour(null);
      setCurrentStepIndex(0);
    }
  }, [currentStepIndex, markTourComplete]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) setCurrentStepIndex((i) => i - 1);
  }, [currentStepIndex]);

  const skipTour = useCallback(() => {
    setActiveTour(null);
    setCurrentStepIndex(0);
  }, []);

  const completeTour = useCallback(() => {
    if (activeTourRef.current) {
      markTourComplete(activeTourRef.current.id);
    }
    setActiveTour(null);
    setCurrentStepIndex(0);
  }, [markTourComplete]);

  const isTourCompleted = useCallback(
    (tourId: string) => state.completedTours.includes(tourId),
    [state.completedTours],
  );

  const resetOnboarding = useCallback(() => {
    setState({ ...DEFAULT_STATE });
    setActiveTour(null);
    setCurrentStepIndex(0);
  }, []);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      checklistItems,
      checklistDismissed: state.checklistDismissed,
      completeChecklistItem,
      dismissChecklist,
      showChecklist,
      checklistProgress,
      activeTour: stepReady ? activeTour : null,
      currentStepIndex,
      isTouring: activeTour !== null,
      startTour,
      startModuleTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      isTourCompleted,
      resetOnboarding,
    }),
    [
      checklistItems,
      state.checklistDismissed,
      completeChecklistItem,
      dismissChecklist,
      showChecklist,
      checklistProgress,
      activeTour,
      currentStepIndex,
      stepReady,
      startTour,
      startModuleTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      isTourCompleted,
      resetOnboarding,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}
